import { TFunction } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { MappedStakeholder, StakeholderRoleEnum, StakeholderType } from "src/core/constants/folder";
import { ISelectOption } from "src/core/constants/options";
import { useFolder, useFolderCorporationId } from "src/core/contexts/FolderContext";
import { useRiskOptions } from "src/core/contexts/RiskOptionsContext";
import type { AddStakeholderBasicFormValues } from "src/core/forms/stakeholder/addStakeholderBasic.schema";
import type { UpdateStakeholderFullFormValues } from "src/core/forms/stakeholder/updateStakeholderFull.schema";
import {
  CompareCustomerGraphWithExternalDataQuery,
  DataType,
  InputValueInput,
  useAddStakeholderMutation,
  useDeleteStakeholderMutation,
  useUpdateCorporationMutation,
  useUpdateStakeholderMutation,
} from "src/core/gql/graphql";
import { useCountryOptions } from "src/core/hooks/common/useCountryOptions";
import { useLegalStatusOptions } from "src/core/hooks/common/useLegalStatusOptions";
import {
  buildCreateStakeholderVariables,
  buildUpdateStakeholderVariables as buildUpdateStakeholderMutationVariables,
} from "src/core/utils/stakeholders";

type StakeholderWithDiffsFromQuery = NonNullable<
  CompareCustomerGraphWithExternalDataQuery["compareCustomerGraphWithExternalData"]
>["stakeholdersWithDiffs"][number];

type FolderStakeholder = NonNullable<StakeholderWithDiffsFromQuery["folderStakeholder"]>;
type ExternalData = StakeholderWithDiffsFromQuery["externalData"][number];

export type ModificationType = "ADDED" | "DELETED" | "UPDATED";

export type DiffInputValue = {
  fieldCode: string;
  oldRawValue: string;
  newRawValue: string;
  oldValue: string;
  newValue: string;
};

export type SelectedValueSide = "old" | "new";

export type StakeholderSelectionKey = string;

export type StakeholderProfileAction = "keep" | "add" | "remove";

const COUNTRY_FIELD_CODES = new Set([
  "citizenshipCountryCode",
  "residenceCountryCode",
  "birthCountryCode",
  "customCountryCode1",
  "registrationCountryCode",
]);

const LEGAL_STATUS_FIELD_CODES = new Set(["customListCode2"]);
const LEGAL_TYPE_FIELD_CODES = new Set(["entityTypeCode"]);
const ACTIVITY_FIELD_CODES = new Set(["activityCode"]);

function getOptionLabelByValue(options: ISelectOption[], value: string): string {
  return options.find((opt) => opt.value === value)?.label ?? value;
}

function resolveDisplayValue(
  fieldCode: string,
  rawValue: string,
  mapping: {
    countryOptions: ISelectOption[];
    legalStatusOptions: ISelectOption[];
    legalTypesWithRiskOptions: ISelectOption[];
    activityWithRiskOptions: ISelectOption[];
  }
): string {
  if (!rawValue) return "—";

  if (COUNTRY_FIELD_CODES.has(fieldCode)) {
    return getOptionLabelByValue(mapping.countryOptions, rawValue);
  }
  if (LEGAL_STATUS_FIELD_CODES.has(fieldCode)) {
    return getOptionLabelByValue(mapping.legalStatusOptions, rawValue);
  }
  if (LEGAL_TYPE_FIELD_CODES.has(fieldCode)) {
    return getOptionLabelByValue(mapping.legalTypesWithRiskOptions, rawValue);
  }
  if (ACTIVITY_FIELD_CODES.has(fieldCode)) {
    return getOptionLabelByValue(mapping.activityWithRiskOptions, rawValue);
  }

  return rawValue;
}

function getDiffInputValues(
  stakeholder: StakeholderWithDiffsFromQuery,
  mapping: {
    countryOptions: ISelectOption[];
    legalStatusOptions: ISelectOption[];
    legalTypesWithRiskOptions: ISelectOption[];
    activityWithRiskOptions: ISelectOption[];
  }
): DiffInputValue[] | null {
  const folder = stakeholder.folderStakeholder;
  const external = stakeholder.externalData?.[0];
  if (folder == null || !external || stakeholder.externalData.length === 0) {
    return null;
  }
  const folderByCode = new Map(
    folder.inputValues.map((iv) => [iv.inputField.fieldCode, iv.stringValue ?? ""])
  );
  const externalFull = external.fullInputValues ?? [];
  const result: DiffInputValue[] = [];
  for (const ext of externalFull) {
    const code = ext.fieldCode;
    if (!folderByCode.has(code)) continue;
    const oldRawValue = folderByCode.get(code) ?? "";
    const newRawValue = ext.value ?? "";
    if (oldRawValue !== newRawValue) {
      result.push({
        fieldCode: code,
        oldRawValue,
        newRawValue,
        oldValue: resolveDisplayValue(code, oldRawValue, mapping),
        newValue: resolveDisplayValue(code, newRawValue, mapping),
      });
    }
  }
  return result;
}

function computeCountDifferencesFound(
  stakeholdersWithDiffs: StakeholderWithDiffsFromQuery[],
  mapping: {
    countryOptions: ISelectOption[];
    legalStatusOptions: ISelectOption[];
    legalTypesWithRiskOptions: ISelectOption[];
    activityWithRiskOptions: ISelectOption[];
  }
): number {
  let count = 0;
  for (const stakeholder of stakeholdersWithDiffs) {
    const hasFolder = stakeholder.folderStakeholder !== null;
    const hasExternal = (stakeholder.externalData?.length ?? 0) > 0;
    if (hasFolder && hasExternal) {
      count += getDiffInputValues(stakeholder, mapping)?.length ?? 0;
    } else {
      count += 1;
    }
  }
  return count;
}

function getRoleLabel(typeCode: string, t: TFunction): string {
  if (!typeCode) return "";
  const enumKeys = Object.keys(StakeholderRoleEnum) as (keyof typeof StakeholderRoleEnum)[];
  const byValue = enumKeys.find((k) => StakeholderRoleEnum[k] === typeCode);
  const labelKey =
    byValue ?? (enumKeys.includes(typeCode as keyof typeof StakeholderRoleEnum) ? typeCode : null);
  return labelKey ? t(`enum.stakeholdersType.${labelKey}`) : typeCode;
}

function getRoleLabelsFromTypeCodes(typeCodes: string[], t: TFunction): string {
  const labels = typeCodes.map((typeCode) => getRoleLabel(typeCode, t)).filter(Boolean);
  return labels.join(", ");
}

export const useViewModification = (
  compareData:
    | CompareCustomerGraphWithExternalDataQuery["compareCustomerGraphWithExternalData"]
    | undefined,
  onFolderAccess: () => void
) => {
  const { t } = useTranslation();

  const corporationId = useFolderCorporationId();
  const { refetch, isPPH } = useFolder();
  const { countryOptions } = useCountryOptions();
  const { legalStatusOptions } = useLegalStatusOptions();
  const { legalTypesWithRiskOptions, activityWithRiskOptions } = useRiskOptions();

  const [updateStakeholder] = useUpdateStakeholderMutation();
  const [addStakeholder] = useAddStakeholderMutation();
  const [deleteStakeholder] = useDeleteStakeholderMutation();
  const [updateCorporation] = useUpdateCorporationMutation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [countDifferencesFound, setCountDifferencesFound] = useState(0);
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());
  const [selectedValueSideByStakeholderKey, setSelectedValueSideByStakeholderKey] = useState<
    Record<StakeholderSelectionKey, Record<string, SelectedValueSide>>
  >({});

  const [selectedProfileActionByStakeholderKey, setSelectedProfileActionByStakeholderKey] =
    useState<Record<StakeholderSelectionKey, StakeholderProfileAction>>({});

  const getAddedProfileTypeLabel = useCallback(
    (rolesToAdd: { typeCode?: string | null }[] | undefined): string => {
      const codes = (rolesToAdd ?? [])
        .map((r) => r.typeCode)
        .filter((code): code is string => code != null && code !== "");
      return codes.length > 0
        ? getRoleLabelsFromTypeCodes(codes, t)
        : t("modal.viewModifications.unknownProfileType", "—");
    },
    [t]
  );

  const toggleExpanded = useCallback((index: number) => {
    setExpandedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  useEffect(() => {
    const list = compareData?.stakeholdersWithDiffs ?? [];
    setCountDifferencesFound(
      computeCountDifferencesFound(list, {
        countryOptions,
        legalStatusOptions,
        legalTypesWithRiskOptions,
        activityWithRiskOptions,
      })
    );
  }, [
    compareData,
    countryOptions,
    legalStatusOptions,
    legalTypesWithRiskOptions,
    activityWithRiskOptions,
  ]);

  const getSelectedValueSide = useCallback(
    (stakeholderKey: StakeholderSelectionKey, fieldCode: string): SelectedValueSide => {
      return selectedValueSideByStakeholderKey[stakeholderKey]?.[fieldCode] ?? "new";
    },
    [selectedValueSideByStakeholderKey]
  );

  const setSelectedValueSide = useCallback(
    (stakeholderKey: StakeholderSelectionKey, fieldCode: string, side: SelectedValueSide) => {
      setSelectedValueSideByStakeholderKey((prev) => {
        const prevByField = prev[stakeholderKey] ?? {};
        if (prevByField[fieldCode] === side) return prev;
        return {
          ...prev,
          [stakeholderKey]: {
            ...prevByField,
            [fieldCode]: side,
          },
        };
      });
    },
    []
  );

  const resetSelectedValueSides = useCallback(() => {
    setSelectedValueSideByStakeholderKey({});
    setSelectedProfileActionByStakeholderKey({});
  }, []);

  const getSelectedProfileAction = useCallback(
    (
      stakeholderKey: StakeholderSelectionKey,
      modificationType: ModificationType
    ): StakeholderProfileAction => {
      const explicit = selectedProfileActionByStakeholderKey[stakeholderKey];
      if (explicit) return explicit;

      if (modificationType === "ADDED") return "add";
      if (modificationType === "DELETED") return "remove";
      return "keep";
    },
    [selectedProfileActionByStakeholderKey]
  );

  const setSelectedProfileAction = useCallback(
    (stakeholderKey: StakeholderSelectionKey, action: StakeholderProfileAction) => {
      setSelectedProfileActionByStakeholderKey((prev) => {
        if (prev[stakeholderKey] === action) return prev;
        return { ...prev, [stakeholderKey]: action };
      });
    },
    []
  );

  const getName = useCallback(
    (stakeholder: StakeholderWithDiffsFromQuery) => {
      if (stakeholder.folderStakeholder !== null) {
        if (stakeholder.folderStakeholder?.isFolderCustomer && !isPPH) {
          return (
            stakeholder.folderStakeholder?.inputValues.find(
              (input) => input.inputField.fieldCode === "name"
            )?.stringValue ?? ""
          );
        } else {
          return (
            (stakeholder.folderStakeholder?.inputValues.find(
              (input) => input.inputField.fieldCode === "firstName"
            )?.stringValue ?? "") +
            " " +
            (stakeholder.folderStakeholder?.inputValues.find(
              (input) => input.inputField.fieldCode === "lastName"
            )?.stringValue ?? "")
          );
        }
      } else {
        if (stakeholder.externalData[0].isCorporation) {
          return (
            stakeholder.externalData[0].fullInputValues.find((input) => input.fieldCode === "name")
              ?.value ?? ""
          );
        } else {
          return (
            (stakeholder.externalData[0].fullInputValues.find(
              (input) => input.fieldCode === "firstName"
            )?.value ?? "") +
            " " +
            (stakeholder.externalData[0].fullInputValues.find(
              (input) => input.fieldCode === "lastName"
            )?.value ?? "")
          );
        }
      }
    },
    [isPPH]
  );

  const formatData = useCallback(() => {
    return (compareData?.stakeholdersWithDiffs ?? []).map((stakeholder) => {
      const modificationType: ModificationType =
        stakeholder.folderStakeholder !== null && stakeholder.externalData.length > 0
          ? "UPDATED"
          : stakeholder.folderStakeholder === null && stakeholder.externalData.length > 0
          ? "ADDED"
          : "DELETED";
      const diffInputValues = getDiffInputValues(stakeholder, {
        countryOptions,
        legalStatusOptions,
        legalTypesWithRiskOptions,
        activityWithRiskOptions,
      });
      const count = diffInputValues !== null ? diffInputValues.length : 1;
      return {
        name: getName(stakeholder),
        count,
        stakeholder,
        modificationType,
        diffInputValues,
      };
    });
  }, [
    compareData,
    getName,
    countryOptions,
    legalStatusOptions,
    legalTypesWithRiskOptions,
    activityWithRiskOptions,
  ]);

  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      const rows = formatData();
      const operations: Promise<unknown>[] = [];

      const buildDeleteStakeholderVariables = (folderStakeholder: FolderStakeholder) => {
        if (!corporationId) {
          throw new Error("Aucun id de société trouvé");
        }

        const isLegalRep = folderStakeholder.isLegalRepresentative;
        const isDirectBO = folderStakeholder.beneficiaryOwner;

        return {
          sourceEntityId: folderStakeholder.entity.id,
          targetEntityId: corporationId,
          isLegalRep,
          isDirectBO,
          roleTypeCode_1: isLegalRep ? "legal_representative" : null,
          roleTypeCode_2: isDirectBO ? "direct_beneficiary_owner" : null,
        };
      };

      const buildUpdateCorporationVariables = (
        external: ExternalData,
        newDiffs: DiffInputValue[]
      ) => {
        const inputValues: {
          dataType: DataType;
          fieldCode: string;
          value: string;
        }[] = newDiffs.map((diff) => {
          const extValue = external.fullInputValues.find((iv) => iv.fieldCode === diff.fieldCode);
          return {
            dataType: extValue?.dataType ?? DataType.String,
            fieldCode: diff.fieldCode,
            value: diff.newRawValue ?? "",
          };
        });

        if (!corporationId) {
          throw new Error("Aucun id de société trouvé");
        }

        return { corporationId, inputValues };
      };

      for (const [index, row] of rows.entries()) {
        const stakeholderKey = row.stakeholder.folderStakeholder?.entity.id ?? String(index);
        const selectedProfileAction = getSelectedProfileAction(
          stakeholderKey,
          row.modificationType
        );

        if (row.modificationType === "ADDED") {
          if (selectedProfileAction !== "add") continue;
          const external = row.stakeholder.externalData?.[0];
          if (!external) continue;
          if (!corporationId) {
            throw new Error("Aucun id de société trouvé");
          }

          const roles = (external.rolesToAdd ?? []).map(
            (r) => StakeholderRoleEnum[r.typeCode as keyof typeof StakeholderRoleEnum]
          );

          const firstName =
            external.fullInputValues.find((iv) => iv.fieldCode === "firstName")?.value ?? "";
          const lastName =
            external.fullInputValues.find((iv) => iv.fieldCode === "lastName")?.value ?? "";

          const addData: AddStakeholderBasicFormValues = {
            roles,
            firstName,
            lastName,
          };

          const extraInputValues: InputValueInput[] = external.fullInputValues
            .filter((iv) => iv.fieldCode !== "firstName" && iv.fieldCode !== "lastName")
            .map((iv) => ({
              dataType: iv.dataType,
              fieldCode: iv.fieldCode,
              value: iv.value ?? undefined,
            }));

          operations.push(
            addStakeholder({
              variables: buildCreateStakeholderVariables(corporationId, addData, extraInputValues),
            })
          );
        }

        if (row.modificationType === "DELETED") {
          if (selectedProfileAction !== "remove") continue;
          if (!row.stakeholder.folderStakeholder) continue;

          operations.push(
            deleteStakeholder({
              variables: buildDeleteStakeholderVariables(row.stakeholder.folderStakeholder),
            })
          );
        }

        if (row.modificationType === "UPDATED") {
          if (!row.stakeholder.folderStakeholder) continue;
          const external = row.stakeholder.externalData?.[0];
          if (!external) continue;

          // On n'applique que les diffs sélectionnées côté "new".
          const newDiffs = (row.diffInputValues ?? []).filter(
            (diff) => getSelectedValueSide(stakeholderKey, diff.fieldCode) === "new"
          );

          if (newDiffs.length === 0) continue;

          if (row.stakeholder.folderStakeholder.isFolderCustomer) {
            operations.push(
              updateCorporation({
                variables: buildUpdateCorporationVariables(external, newDiffs),
              })
            );
          } else {
            const folderStakeholder = row.stakeholder.folderStakeholder;

            // Valeurs actuelles côté dossier.
            const currentValuesByCode = new Map(
              folderStakeholder.inputValues.map((iv) => [
                iv.inputField.fieldCode,
                iv.stringValue ?? "",
              ])
            );

            // Appliquer uniquement les champs sélectionnés en "new".
            const newValuesByCode = new Map(newDiffs.map((d) => [d.fieldCode, d.newRawValue]));
            const getCurrentValue = (code: string) => currentValuesByCode.get(code) ?? "";
            const getUpdatedValue = (code: string) =>
              newValuesByCode.get(code) ?? getCurrentValue(code);

            if (!corporationId) {
              throw new Error("Aucun id de société trouvé");
            }

            const roles = [
              ...(folderStakeholder.isLegalRepresentative
                ? [StakeholderRoleEnum.legal_representative]
                : []),
              ...(folderStakeholder.beneficiaryOwner
                ? [StakeholderRoleEnum.direct_beneficiary_owner]
                : []),
            ];

            const selectedStakeholder: MappedStakeholder = {
              id: folderStakeholder.entity.id,
              type: StakeholderType.Person,
              roles,
              isFolderCustomer: folderStakeholder.isFolderCustomer,
              isLegalRepresentative: folderStakeholder.isLegalRepresentative,
              isBeneficiaryOwner: folderStakeholder.beneficiaryOwner,
              firstName: getCurrentValue("firstName"),
              lastName: getCurrentValue("lastName"),
              birthDate: getCurrentValue("birthDate"),
              documents: [],
              missingRequiredFieldCount: 0,
              missingDocumentCount: 0,
              duplicateCheckDone: false,
              duplicateCheckLoading: false,
              duplicateCheckFetched: true,
            };

            const updateData: UpdateStakeholderFullFormValues = {
              title: getUpdatedValue("title"),
              firstName: getUpdatedValue("firstName"),
              birthName: getUpdatedValue("birthName"),
              lastName: getUpdatedValue("lastName"),
              roles,
              citizenshipCountryCode: getUpdatedValue("citizenshipCountryCode"),
              birthDate: getUpdatedValue("birthDate"),
              residenceCountryCode: getUpdatedValue("residenceCountryCode"),
              birthCountryCode: getUpdatedValue("birthCountryCode"),
              birthPlace: getUpdatedValue("birthPlace"),
              documents: {} as UpdateStakeholderFullFormValues["documents"],
            };

            operations.push(
              updateStakeholder({
                variables: buildUpdateStakeholderMutationVariables(
                  corporationId,
                  selectedStakeholder,
                  updateData
                ),
              })
            );
          }
        }
      }

      await Promise.all(operations);
      await refetch();
    } catch (error) {
      console.error("useViewModification::handleSubmit error", error);
    } finally {
      setIsLoading(false);
      await onFolderAccess();
    }
  }, [
    formatData,
    corporationId,
    getSelectedProfileAction,
    getSelectedValueSide,
    addStakeholder,
    deleteStakeholder,
    updateCorporation,
    updateStakeholder,
    onFolderAccess,
    refetch,
  ]);

  return {
    countDifferencesFound,
    formatData,
    getAddedProfileTypeLabel,
    expandedIndices,
    toggleExpanded,
    selectedValueSideByStakeholderKey,
    selectedProfileActionByStakeholderKey,
    getSelectedValueSide,
    setSelectedValueSide,
    getSelectedProfileAction,
    setSelectedProfileAction,
    resetSelectedValueSides,
    handleSubmit,
    isLoading,
  };
};
